import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./InsertRenewalRecord.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack";

const API = "https://www.lianfoong.com/api/renewals/payments";
const localDateTime = (value) => value ? String(value).substring(0, 16) : "";
const money = (n) => Number(n).toFixed(2);

// Uses the same whole-calendar-month rule as Renew (capped at eight months).
function suggestedInterest(oldPrincipal, oldGrant, newGrant, discount) {
  if (oldPrincipal === "" || oldPrincipal == null || !oldGrant || !newGrant) return null;
  const principal = Number(oldPrincipal);
  if (!Number.isFinite(principal) || principal <= 0) return null;
  const old = new Date(oldGrant);
  const renewed = new Date(newGrant);
  if (Number.isNaN(old.getTime()) || Number.isNaN(renewed.getTime()) || renewed < old) return null;
  const monthsRaw = (renewed.getFullYear() - old.getFullYear()) * 12
    + renewed.getMonth() - old.getMonth()
    + (renewed.getDate() > old.getDate() ? 1 : 0);
  const months = Math.min(8, Math.max(1, monthsRaw));
  const rate = Math.max(0, 0.015 * months - (discount ? 0.005 : 0));
  return { months, amount: money(Math.round((principal * rate + Number.EPSILON) * 100) / 100) };
}

function getError(err) {
  const body = err?.response?.data;
  if (typeof body === "string") return body;
  return body?.message || body?.error || "Could not load the renewal details. Please check the receipt number.";
}

export default function InsertRenewalRecord() {
  useEscapeToGoBack();
  const navigate = useNavigate();
  const [newReceiptInput, setNewReceiptInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [oldReceiptNo, setOldReceiptNo] = useState("");
  const [oldGrantDateTime, setOldGrantDateTime] = useState("");
  const [oldPrincipal, setOldPrincipal] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDateTime, setPaymentDateTime] = useState("");
  const [firstMonthDiscount, setFirstMonthDiscount] = useState(false);
  const [renewalSource, setRenewalSource] = useState("INSTORE");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const calc = preview && suggestedInterest(oldPrincipal, oldGrantDateTime,
    localDateTime(preview.newGrantDateTime), firstMonthDiscount);

  const loadDetails = async (oldOverride = "", discountOverride = undefined) => {
    if (!newReceiptInput.trim()) { setError("Enter the new receipt number first."); return; }
    setLoading(true);
    setError("");
    setSuccess("");
    // Drop stale summary immediately. Staff must look up the new receipt again after changing it.
    setPreview(null);
    try {
      const { data } = await axios.get(`${API}/preview-insert`, {
        params: {
          newReceiptNo: newReceiptInput.trim(),
          ...(oldOverride.trim() ? { oldReceiptNo: oldOverride.trim() } : {}),
          ...(discountOverride === undefined ? {} : { firstMonthDiscount: discountOverride }),
        },
        withCredentials: true,
      });
      setPreview(data);
      setNewReceiptInput(data.newReceiptNo);
      setOldReceiptNo(data.oldReceiptNo || "");
      setOldGrantDateTime(localDateTime(data.oldGrantDateTime));
      setOldPrincipal(data.oldPrincipal == null ? "" : String(data.oldPrincipal));
      setPaymentDateTime(localDateTime(data.paymentDateTime));
      setFirstMonthDiscount(Boolean(data.firstMonthDiscount));
      setPaymentAmount(data.suggestedInterest == null ? "" : money(data.suggestedInterest));
    } catch (err) {
      setError(getError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!preview) { setError("Look up the new receipt and review the details before inserting."); return; }
    if (preview.alreadyRecorded) { setError("This ticket already has a payment record."); return; }
    if (!oldReceiptNo.trim()) { setError("Old receipt number is required. Enter it manually if missing."); return; }
    const amount = Number(paymentAmount);
    if (!paymentAmount.trim() || !/^\d+(?:\.\d{1,2})?$/.test(paymentAmount.trim())
        || !Number.isFinite(amount)) {
      setError("Enter a valid collected interest amount (e.g. 12.50), not an empty field."); return;
    }
    if (!paymentDateTime) { setError("Payment date and time are required."); return; }
    if (preview.oldReceiptNo && oldReceiptNo !== preview.oldReceiptNo) {
      setError("Old receipt was changed. Click 'Refresh details' before inserting."); return;
    }
    const notes = [];
    if (calc && Math.abs(Number(calc.amount) - amount) >= 0.01) {
      notes.push(`Entered $${money(amount)} differs from calculated $${calc.amount}.`);
    }
    if (amount === 0) notes.push("FREE RENEWAL: Confirm no interest was collected. This creates a $0.00 renewal payment record.");
    if (paymentDateTime.slice(0, 10) !== String(preview.newGrantDateTime || "").slice(0, 10)) {
      notes.push("Payment date differs from the new ticket's grant date.");
    }
    if (!calc) notes.push("Interest could not be verified using the old grant date and principal.");
    const message = `Insert missing payment for ${preview.newReceiptNo}?\n\nOld receipt: ${oldReceiptNo}\nInterest collected: $${money(amount)}\nPayment date: ${paymentDateTime.replace("T", " ")}\nSource: ${renewalSource}`;
    if (!window.confirm(message + (notes.length ? `\n\nPLEASE CHECK:\n${notes.join("\n")}` : ""))) return;

    setSaving(true);
    try {
      await axios.post(`${API}/insert-record`, {
        newReceiptNo: preview.newReceiptNo,
        oldReceiptNo: oldReceiptNo.trim(),
        paymentAmount: amount,
        paymentDateTime: paymentDateTime.length === 16 ? `${paymentDateTime}:00` : paymentDateTime,
        renewalSource,
      }, { withCredentials: true });
      setSuccess(`Payment record inserted for ${preview.newReceiptNo}.`);
      setPreview({ ...preview, alreadyRecorded: true });
    } catch (err) {
      setError(getError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="missing-renewal">
      <h2>Insert Missing Renewal Record</h2>
      <p className="missing-subtitle">Enter the <strong>new receipt number</strong> to retrieve the renewal details.</p>
      <form onSubmit={handleSubmit}>
        <div className="missing-lookup">
          <label htmlFor="missing-new">New receipt number</label>
          <div className="missing-row">
            <input id="missing-new" autoFocus value={newReceiptInput}
              placeholder="0426309 or B/0426/0309"
              onChange={(e) => { setNewReceiptInput(e.target.value.toUpperCase()); setPreview(null); setRenewalSource("INSTORE"); setSuccess(""); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); loadDetails(); } }} />
            <button type="button" disabled={loading || saving} onClick={() => loadDetails()}>
              {loading ? "Loading…" : "Find receipt"}
            </button>
          </div>
          <small>Example: 01261 for B/0126/0001</small>
        </div>

        {preview && <section className="missing-summary" aria-label="Renewal payment details">
          <h3>Review renewal details</h3>
          {preview.alreadyRecorded && <p className="missing-danger">Payment already exists for this receipt. Another payment cannot be inserted.</p>}
          <div className="missing-fields">
            <label>Old receipt number
              <input value={oldReceiptNo} placeholder="Enter old receipt number if missing"
                onChange={(e) => setOldReceiptNo(e.target.value.toUpperCase())}
                onBlur={() => { if (oldReceiptNo.trim() && oldReceiptNo !== preview.oldReceiptNo) loadDetails(oldReceiptNo, firstMonthDiscount); }} />
            </label>
            <label>New ticket grant date &amp; time
              <input value={localDateTime(preview.newGrantDateTime).replace("T", " ") || "Not recorded"} readOnly />
            </label>
            <label>Old ticket grant date &amp; time
              <input type="datetime-local" value={oldGrantDateTime} onChange={(e) => setOldGrantDateTime(e.target.value)} />
            </label>
            <label>Old principal ($)
              <input type="number" step="0.01" min="0" value={oldPrincipal}
                onChange={(e) => setOldPrincipal(e.target.value)} placeholder="Enter principal if missing" />
            </label>
            <label>Months charged
              <input value={calc?.months ?? "Unavailable"} readOnly />
            </label>
            <label>Calculated interest ($)
              <input value={calc?.amount ?? "Unavailable"} readOnly />
            </label>
          </div>
          <label className="missing-checkbox"><input type="checkbox" checked={firstMonthDiscount}
            onChange={(e) => setFirstMonthDiscount(e.target.checked)} /> Apply first-month 0.5% discount</label>
          <button className="missing-secondary" type="button" disabled={loading || saving}
            onClick={() => {
              if (oldReceiptNo.trim() !== preview.oldReceiptNo) { loadDetails(oldReceiptNo, firstMonthDiscount); return; }
              if (calc) setPaymentAmount(calc.amount);
              else setError("Enter a valid old grant date and principal before recalculating.");
            }}>Use calculated amount / Refresh details</button>
          <div className="missing-fields missing-payment">
            <label>Interest actually collected ($)
              <input type="number" required step="0.01" min="0" value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)} placeholder="Enter amount collected" />
              <small>Enter 0 if the shop waived the interest. Do not leave it blank.</small>
            </label>
            <label>Payment date &amp; time
              <input type="datetime-local" required value={paymentDateTime}
                onChange={(e) => setPaymentDateTime(e.target.value)} />
            </label>
            <label>Renewal source
              <select value={renewalSource} onChange={(e) => setRenewalSource(e.target.value)}>
                <option value="INSTORE">In-store (default)</option>
                <option value="ONLINE">Online</option>
              </select>
            </label>
          </div>
          <small className="missing-footnote">The calculated interest is an estimate based on the ticket dates and principal. Any manual changes here affect only the payment record, not the original pawn ticket. Confirm the actual amount collected.</small>
          {preview.warnings?.length > 0 && <div className="missing-warnings">{preview.warnings.map((warning, i) => <p key={i}>{warning}</p>)}</div>}
          <button className="missing-submit" type="submit" disabled={loading || saving || preview.alreadyRecorded}>
            {saving ? "Inserting…" : "Insert renewal payment"}
          </button>
        </section>}
        {error && <p role="alert" className="missing-danger">{error}</p>}
        {success && <p role="status" className="missing-success">{success}</p>}
      </form>
      <button className="missing-back" type="button" onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
    </main>
  );
}
